import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import {
  FAMILLE_PERSONNES,
  PersonneSchema,
  statutDePersonne,
  type Personne,
  type StatutDePersonne,
} from '@taki/data-model';
import { createConverter } from './standard-fields.js';
import { type ArchivedMode } from './list-query.js';

/**
 * L'ANNUAIRE DES PERSONNES : `orgs/{orgId}/personnes/{personId}`, une entree
 * par personne de l'org (nom, e-mail, uid une fois connectee). Le modele et le
 * pourquoi vivent dans `packages/data-model/src/personne.ts`.
 *
 * CE REPOSITORY NE FAIT QUE LIRE. L'entree s'ecrit avec la fiche membre, dans
 * le MEME LOT, par celui qui ecrit la fiche (`members.ts` cote client, les
 * Functions et les scripts de leur cote) : un annuaire qu'on ecrirait a part
 * finirait par dire autre chose que les fiches, et aucun trigger ne tourne en
 * production pour rattraper l'ecart.
 *
 * Ce n'est PAS une famille (pas de `type`, pas de porte, pas d'agregat) :
 * `repositoryOf('personnes')` la refuse, comme `members`.
 */

/** Nom de la sous-collection Firestore de l'annuaire. */
export const PERSONNES_COLLECTION = FAMILLE_PERSONNES;

/** Le chemin de l'annuaire d'une org : `orgs/{orgId}/personnes`. */
export function cheminPersonnes(orgId: string): string {
  return `orgs/${orgId}/${PERSONNES_COLLECTION}`;
}

/**
 * UN `personId` NEUF : un id Firestore tire au sort cote client, sans aller-
 * retour serveur. Le meme generateur que les Functions et les scripts
 * (`collection(...).doc().id`), donc le meme alphabet partout.
 */
export function personIdNeuf(db: Firestore, orgId: string): string {
  return doc(collection(db, cheminPersonnes(orgId))).id;
}

/**
 * Une personne telle que le SDK la rend : l'entree, plus son STATUT, qui n'est
 * pas stocke mais DEDUIT de `userId` (un fait ecrit deux fois divergerait).
 */
export type PersonneLue = Personne & { statut: StatutDePersonne };

/** Repository de lecture de l'annuaire des personnes. */
export interface PersonneRepository {
  /**
   * Tout l'annuaire de l'org, trie par nom (l'e-mail a defaut de nom).
   * `archived` vaut `exclude` par defaut ; l'annuaire d'affichage demande
   * `all`, pour nommer aussi les personnes retirees sur les fiches du passe.
   */
  list(options?: { archived?: ArchivedMode }): Promise<PersonneLue[]>;
  /** L'entree d'un `personId`, ou `null`. */
  get(personId: string): Promise<PersonneLue | null>;
}

const personneConverter = createConverter<Personne>((raw: DocumentData) => PersonneSchema.parse(raw));

function avecStatut(personne: Personne): PersonneLue {
  return { ...personne, statut: statutDePersonne(personne) };
}

/** Le mot qui range une personne : son nom, a defaut son e-mail. */
function clefDeTri(personne: Personne): string {
  return personne.displayName.trim() !== '' ? personne.displayName : personne.email;
}

export function createPersonneRepository(db: Firestore, orgId: string): PersonneRepository {
  const col = collection(db, cheminPersonnes(orgId)).withConverter(personneConverter);

  return {
    async list(options?: { archived?: ArchivedMode }): Promise<PersonneLue[]> {
      const mode = options?.archived ?? 'exclude';
      // Comme l'annuaire des membres : l'org est dans le CHEMIN, un seul filtre
      // d'egalite au plus, donc AUCUN index composite, et le tri en memoire
      // (quelques dizaines de lignes, jamais paginees).
      const contraintes =
        mode === 'exclude'
          ? [where('archivedAt', '==', null)]
          : mode === 'only'
            ? [where('archivedAt', '!=', null)]
            : [];
      const snap = await getDocs(query(col, ...contraintes));
      const personnes: PersonneLue[] = [];
      for (const d of snap.docs) {
        // UNE ENTREE ABIMEE NE PRIVE PAS L'ORG DE TOUS SES NOMS : elle est
        // ecartee (et dite en console), les autres s'affichent. Le schema reste
        // le juge, on ne lit rien qu'il refuse.
        try {
          personnes.push(avecStatut(d.data()));
        } catch (cause) {
          console.warn(`[taki/personnes] entree ${d.id} illisible, ecartee de l'annuaire`, cause);
        }
      }
      return personnes.sort((a, b) => clefDeTri(a).localeCompare(clefDeTri(b), 'fr-FR'));
    },

    async get(personId: string): Promise<PersonneLue | null> {
      const snap = await getDoc(doc(col, personId));
      if (!snap.exists()) return null;
      return avecStatut(snap.data());
    },
  };
}
